import React, { useEffect, useState } from "react";
import {
  getIsAuthenticatedSelector,
  checkAuthState,
  logout,
  useAuthenticationDispatch,
  useUserDataPreloader,
} from "@quest-finance/quest-fe-shared/dist/auth";
import Loading from "@quest-finance/quest-fe-shared/dist/common/components/Loading";
import NotFoundPage from "@quest-finance/quest-fe-shared/dist/common/components/NotFoundPage";
import ScrollToTop from "@quest-finance/quest-fe-shared/dist/common/components/ScrollToTop";
import APIService from "@quest-finance/quest-fe-shared/dist/common/services/APIService";
import { PageRoute } from "@quest-finance/quest-fe-shared/dist/common/types/PageRoute";
import { Notification } from "@quest-finance/quest-fe-shared/dist/notification";
import { AutoPageReload } from "@quest-finance/quest-fe-shared/dist/version-manager";
import { useSelector } from "react-redux";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router";
import MoveApplicationPage from "../../application/pages/MoveApplication/MoveApplication";
import { creditAssessmentRoot, CreditEngineRoot } from "../../assessment-v2";
import { LayoutContext } from "../contexts/LayoutContext";
import { UserLogin, ForgotPasswordPage } from "../pages/User";

const isProduction = process.env.NODE_ENV === "production";
const BaseLayout = React.lazy(() => import("./BaseLayout"));

const Utils = () => {
  const location = useLocation();

  return (
    <>
      <Notification maxVisible={2} />
      <ScrollToTop locationPathname={location.pathname} />
      {isProduction && <AutoPageReload location={location} />}
    </>
  );
};

type BaseRouteProps = {
  bannerRefreshShown?: boolean;
  routes: {
    publicRoutes: PageRoute[];
    privateRoutes: PageRoute[];
    antdLayoutPrivateRoutes: PageRoute[];
  };
};
const BaseRoute: React.FunctionComponent<BaseRouteProps> = ({
  routes,
}: BaseRouteProps) => {
  const isAuthenticated = useSelector(getIsAuthenticatedSelector);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const authDispatch = useAuthenticationDispatch();
  const preloader = useUserDataPreloader();

  useEffect(() => {
    authDispatch(checkAuthState());
    APIService.onTokenRefresh = (error) => {
      if (error) {
        window.location.href = "/auth/login";
        authDispatch(logout());
      }

      authDispatch(checkAuthState());
    };
  }, [authDispatch, isAuthenticated]);

  if (preloader.initialising) {
    return <Loading />;
  }

  return (
    <LayoutContext.Provider
      value={{
        isSidebarMinimized,
        setIsSidebarMinimized,
        showSidebar,
        setShowSidebar,
      }}
    >
      <Notification maxVisible={2} />
      <BrowserRouter>
        <Utils />
        <React.Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/auth/login" element={<UserLogin />} />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordPage />}
            />
            {routes.publicRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={<route.component />}
              />
            ))}
            <Route
              path="/application/applications/move"
              element={<MoveApplicationPage />}
            />
            {routes.privateRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={
                  <BaseLayout>
                    <route.component />
                  </BaseLayout>
                }
              />
            ))}
            {routes.antdLayoutPrivateRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={<route.component />}
              />
            ))}
            <Route
              path="/"
              element={
                <Navigate
                  to={!isAuthenticated ? "/auth/login" : "/home"}
                  replace
                />
              }
            />
            <Route path={creditAssessmentRoot} element={<CreditEngineRoot />} />
            <Route
              path="*"
              element={
                <NotFoundPage
                  redirectLink={!isAuthenticated ? "/auth/login" : "/home"}
                  redirectLinkText={
                    !isAuthenticated ? "Go to login page" : "Go to dashboard"
                  }
                />
              }
            />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </LayoutContext.Provider>
  );
};

export default BaseRoute;
